import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { X, FileText, Wand2, MousePointer2, Send, Trash2, Loader2, Search, User, Users, FileSignature, Sparkles, History, ArrowRightLeft, Lock } from "lucide-react";
import { api } from "../lib/api";

// Import pdfjs-dist for in-browser rendering. Point the worker at a
// version-pinned CDN URL — reliable on Kubernetes ingress and CRA without
// custom webpack loader config.
import * as pdfjs from "pdfjs-dist/build/pdf";

pdfjs.GlobalWorkerOptions.workerSrc =
  `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.js`;

// Small helper icon: signature glyph with a lock overlay — used to mark
// signature/initial fields as "DocuSign only, hands off" in the UI.
function FileSignatureLock({ className = "h-3 w-3" }) {
  return (
    <span className={`inline-flex items-center gap-0.5 ${className}`} aria-hidden="true">
      <FileSignature className="h-3 w-3" />
      <Lock className="h-2.5 w-2.5" />
    </span>
  );
}

/**
 * PdfFillDialog — end-to-end PDF filling.
 *
 * 1. Calls `/documents/{id}/inspect` on open.
 * 2. If fillable → SmartFillPanel (dynamic form).
 * 3. If not → ManualOverlayPanel (visual editor over PDF.js).
 * 4. On submit → creates a new Document row, offers a "Send for signature"
 *    handoff via the parent's `onSendForSignature(new_doc_id)` callback.
 *
 * All PDF handling is client-side (blob URLs) — the backend only sees the
 * final structured payload of field values or overlay coordinates.
 */
export default function PdfFillDialog({ docId, docName, onClose, onFilled, onSendForSignature }) {
  const [phase, setPhase] = useState("loading"); // loading|smart|manual|done|error
  const [insp, setInsp] = useState(null);
  const [pdfBytes, setPdfBytes] = useState(null);
  const [error, setError] = useState(null);
  const [filledDoc, setFilledDoc] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const [i, dl] = await Promise.all([
          api.get(`/documents/${docId}/inspect`),
          api.get(`/documents/${docId}/download`),
        ]);
        const bytesResp = await fetch(dl.data.url);
        const buf = await bytesResp.arrayBuffer();
        setInsp(i.data);
        setPdfBytes(buf);
        setPhase(i.data.is_fillable ? "smart" : "manual");
      } catch (e) {
        setError(e?.response?.data?.detail || "Could not open PDF");
        setPhase("error");
      }
    })();
  }, [docId]);

  const handleFilled = (doc) => {
    setFilledDoc(doc);
    setPhase("done");
    onFilled?.(doc);
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/50 backdrop-blur-sm flex items-start justify-center p-4 sm:p-8 overflow-y-auto" data-testid="pdf-fill-dialog">
      <div className="w-full max-w-7xl rounded-xl bg-white shadow-2xl">
        <div className="flex items-start justify-between p-5 border-b border-slate-100">
          <div className="min-w-0">
            <div className="text-[10px] font-semibold uppercase tracking-widest text-slate-500">Fill PDF</div>
            <h3 className="font-heading text-xl font-bold text-slate-900 truncate">{docName}</h3>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700 p-1" data-testid="pdf-fill-close"><X className="h-5 w-5" /></button>
        </div>

        {phase === "loading" && (
          <div className="p-12 text-center text-sm text-slate-500 flex items-center justify-center gap-2">
            <Loader2 className="h-4 w-4 animate-spin" /> Analyzing PDF…
          </div>
        )}
        {phase === "error" && (
          <div className="p-8 text-sm text-rose-700 bg-rose-50">Error: {error}</div>
        )}
        {phase === "smart" && insp && (
          <SmartFillPanel docId={docId} inspect={insp} pdfBytes={pdfBytes} onFilled={handleFilled} />
        )}
        {phase === "manual" && pdfBytes && (
          <ManualOverlayPanel docId={docId} pdfBytes={pdfBytes} onFilled={handleFilled} />
        )}
        {phase === "done" && filledDoc && (
          <div className="p-8 space-y-5" data-testid="pdf-fill-done">
            <div className="mx-auto h-14 w-14 rounded-full flex items-center justify-center text-white" style={{ background: "var(--primary-hex)" }}>
              <FileText className="h-7 w-7" />
            </div>
            <div className="text-center">
              <h4 className="font-heading text-2xl font-bold text-slate-900">Filled and saved</h4>
              <p className="mt-1 text-sm text-slate-600"><span className="font-semibold">{filledDoc.name}</span> is now in Documents, linked back to the original.</p>
            </div>
            <div className="flex flex-wrap items-center justify-center gap-3">
              <button onClick={onClose} className="rounded-md border border-slate-300 px-4 py-2 text-sm font-semibold">Done</button>
              {onSendForSignature && (
                <button
                  onClick={() => { onSendForSignature(filledDoc.id); onClose(); }}
                  className="inline-flex items-center gap-2 rounded-md px-4 py-2 text-sm font-semibold text-white"
                  style={{ background: "var(--primary-hex)" }}
                  data-testid="pdf-fill-send-signature"
                >
                  <Send className="h-4 w-4" /> Send for signature (DocuSign)
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Smart Fill ─────────────────────────────────────────────────────────────

function SmartFillPanel({ docId, inspect, pdfBytes, onFilled }) {
  // Signature/initial fields are surfaced by /inspect but strictly read-only —
  // DocuSign owns them. We split them out so the agent sees they exist
  // (transparency) but can't type into them and we never POST values for them.
  const fillableFields = inspect.fields.filter(f => !f.is_signature);
  const signatureFields = inspect.fields.filter(f => f.is_signature);

  const [values, setValues] = useState(() => {
    const seed = {};
    for (const f of fillableFields) seed[f.name] = f.value || "";
    return seed;
  });
  const [autoNames, setAutoNames] = useState(new Set());
  const [subject, setSubject] = useState(null);
  const [counterparty, setCounterparty] = useState(null);
  const [busy, setBusy] = useState(false);
  const [prefilling, setPrefilling] = useState(false);
  const [recents, setRecents] = useState([]);
  const [focusedField, setFocusedField] = useState(null); // name of field the agent is editing

  // Fetch the agent's last 3 auto-fill picks on mount so repeat contracts on
  // the same deal skip the search entirely.
  useEffect(() => {
    let cancelled = false;
    api.get("/subjects/recent", { params: { limit: 3 } })
      .then(({ data }) => { if (!cancelled) setRecents(data || []); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  const runPrefill = async (primary, counter) => {
    setPrefilling(true);
    try {
      const body = {
        subject_type: primary.type,
        subject_id: primary.id,
      };
      if (counter) {
        body.counterparty_subject_type = counter.type;
        body.counterparty_subject_id = counter.id;
      }
      const { data } = await api.post(`/documents/${docId}/smart-fill/prefill`, body);
      setValues(prev => ({ ...prev, ...data.values }));
      setAutoNames(new Set(data.matched_field_names));
      const n = data.matched_field_names.length;
      if (n === 0) {
        toast.warning("No matching fields found — review manually below");
      } else {
        toast.success(`Auto-filled ${n} field${n === 1 ? "" : "s"} — please review before saving`);
      }
      // Refresh recents so the just-picked subject bubbles to the top.
      api.get("/subjects/recent", { params: { limit: 3 } })
        .then(({ data: rc }) => setRecents(rc || []))
        .catch(() => {});
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Prefill failed");
    } finally { setPrefilling(false); }
  };

  const selectSubject = async (s) => {
    setSubject(s);
    // Clear any prior counterparty when the primary changes so we don't
    // silently mix parties from unrelated deals.
    setCounterparty(null);
    await runPrefill(s, null);
  };

  const selectCounterparty = async (s) => {
    setCounterparty(s);
    if (subject) await runPrefill(subject, s);
  };

  // "Chip" click: same effect as picking from search.
  const pickFromRecent = async (r) => {
    const s = {
      type: r.subject_type,
      id: r.subject_id,
      title: r.title,
      subtitle: r.subtitle,
    };
    await selectSubject(s);
  };

  const clearSubject = () => {
    setSubject(null);
    setCounterparty(null);
    setAutoNames(new Set());
  };

  const updateField = (name, val) => {
    setValues(prev => ({ ...prev, [name]: val }));
    if (autoNames.has(name)) {
      setAutoNames(prev => {
        const next = new Set(prev);
        next.delete(name);
        return next;
      });
    }
  };

  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/documents/${docId}/smart-fill`, { values });
      toast.success("Filled and saved");
      onFilled(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Fill failed");
    } finally { setBusy(false); }
  };

  // Show the counterparty picker only when the primary subject actually
  // represents a side of a deal. For a bare Lead (no side), a second
  // picker is meaningless — the agent has no split to make.
  const showCounterpartyPicker = !!subject && !!subject.side;
  const counterSideLabel = subject?.side === "buyer" ? "Seller" : "Buyer";

  return (
    <div className="p-6">
      <div className="rounded-md border-l-2 bg-emerald-50 text-emerald-900 text-sm px-4 py-3 mb-4 flex items-start gap-2" style={{ borderColor: "#059669" }} data-testid="smart-fill-note">
        <Wand2 className="h-4 w-4 mt-0.5 shrink-0" />
        <div><b>Fillable PDF detected</b> — search for a lead, client, or transaction to auto-fill this form. You can edit anything before saving.</div>
      </div>

      {recents.length > 0 && !subject && (
        <div className="mb-3" data-testid="recent-subjects-chips">
          <div className="text-[10px] font-semibold uppercase tracking-widest text-slate-500 mb-1.5 flex items-center gap-1.5">
            <History className="h-3 w-3" /> Recent
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {recents.map(r => {
              const Icon = SUBJECT_ICON[r.subject_type] || User;
              return (
                <button
                  key={`${r.subject_type}-${r.subject_id}`}
                  onClick={() => pickFromRecent(r)}
                  className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-white px-3 py-1 text-xs font-medium text-slate-700 hover:border-slate-400 hover:bg-slate-50 max-w-[240px]"
                  title={r.subtitle || r.title}
                  data-testid={`recent-subject-${r.subject_type}-${r.subject_id}`}
                >
                  <Icon className="h-3 w-3 text-slate-500 shrink-0" />
                  <span className="truncate">{r.title}</span>
                </button>
              );
            })}
          </div>
        </div>
      )}

      <SubjectSearchBar
        onSelect={selectSubject}
        activeSubject={subject}
        onClear={clearSubject}
        loading={prefilling}
      />

      {showCounterpartyPicker && (
        <div className="mt-3" data-testid="counterparty-picker">
          <SubjectSearchBar
            label={
              <span className="inline-flex items-center gap-1.5">
                <ArrowRightLeft className="h-3 w-3" /> {counterSideLabel} · counterparty (optional override)
              </span>
            }
            placeholder={`Search for the ${counterSideLabel.toLowerCase()} — or leave blank to use stored details`}
            onSelect={selectCounterparty}
            activeSubject={counterparty}
            onClear={async () => { setCounterparty(null); if (subject) await runPrefill(subject, null); }}
            loading={prefilling}
            testidPrefix="counterparty"
          />
        </div>
      )}

      {autoNames.size > 0 && (
        <div
          className="mt-4 rounded-md border border-amber-300 bg-amber-50 text-amber-900 text-sm px-4 py-3 flex items-start gap-2"
          data-testid="smart-fill-review-banner"
        >
          <Sparkles className="h-4 w-4 mt-0.5 shrink-0" />
          <div>
            <div className="font-semibold">
              Manual review · {autoNames.size} field{autoNames.size === 1 ? "" : "s"} auto-filled from {subject?.title}
              {counterparty && <> + {counterparty.title}</>}
            </div>
            <div className="text-xs mt-0.5 text-amber-800">Look over every ✨ field and correct anything that doesn't match the contract. Editing a field removes its badge automatically.</div>
          </div>
        </div>
      )}

      <div className="mt-4 grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.1fr)] gap-4">
        {/* ── Left: fields ─────────────────────────────────────────── */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 lg:max-h-[62vh] max-h-[38vh] overflow-y-auto pr-1" data-testid="smart-fill-fields">
          {fillableFields.map(f => {
            const isAuto = autoNames.has(f.name);
            return (
              <div key={f.name} data-testid={`smart-fill-field-${f.name}`}>
                <label className="text-[11px] font-semibold uppercase tracking-widest text-slate-500 flex items-center gap-1.5">
                  <span>{f.label}</span>
                  {f.page !== null && <span className="text-slate-400 font-normal normal-case">· pg {f.page + 1}</span>}
                  {isAuto && (
                    <span
                      className="inline-flex items-center gap-0.5 text-[9px] font-bold uppercase tracking-widest text-amber-800 bg-amber-100 border border-amber-300 rounded-full px-1.5 py-px normal-case"
                      title="Auto-filled from the selected subject — please verify"
                      data-testid={`smart-fill-auto-badge-${f.name}`}
                    >
                      <Sparkles className="h-2.5 w-2.5" /> auto
                    </span>
                  )}
                </label>
                <input
                  value={values[f.name] || ""}
                  onChange={e => updateField(f.name, e.target.value)}
                  onFocus={() => setFocusedField(f.name)}
                  onBlur={() => setFocusedField(prev => (prev === f.name ? null : prev))}
                  className={`mt-1 w-full rounded-md border px-3 py-2 text-sm ${isAuto ? "border-amber-300 bg-amber-50/50" : "border-slate-300"}`}
                />
              </div>
            );
          })}
          {signatureFields.length > 0 && (
            <div className="sm:col-span-2" data-testid="smart-fill-signature-locked">
              <div className="mt-2 rounded-md border border-slate-200 bg-slate-50 p-3">
                <div className="text-[10px] font-semibold uppercase tracking-widest text-slate-500 flex items-center gap-1.5">
                  <FileSignatureLock /> DocuSign handles these — locked
                </div>
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {signatureFields.map(f => (
                    <span
                      key={f.name}
                      className="inline-flex items-center gap-1 rounded-full border border-slate-300 bg-white px-2.5 py-0.5 text-[11px] font-medium text-slate-600"
                      title={`${f.label}${f.page !== null ? ` · pg ${f.page + 1}` : ""}`}
                      data-testid={`smart-fill-sig-locked-${f.name}`}
                    >
                      <FileSignatureLock className="h-3 w-3" /> {f.label}
                    </span>
                  ))}
                </div>
                <div className="mt-2 text-[11px] text-slate-500">
                  Smart Fill never touches signature or initial fields. Send this document via DocuSign and the client will sign these directly.
                </div>
              </div>
            </div>
          )}
        </div>

        {/* ── Right: live PDF preview ───────────────────────────────── */}
        <PdfPreview
          pdfBytes={pdfBytes}
          fields={fillableFields}
          values={values}
          autoNames={autoNames}
          focusedField={focusedField}
        />
      </div>

      <div className="mt-6 flex items-center justify-end gap-3">
        <div className="text-xs text-slate-500 mr-auto">
          {fillableFields.length} fillable · {signatureFields.length > 0 && <>{signatureFields.length} signature · </>}{autoNames.size} auto-filled
        </div>
        <button
          onClick={submit}
          disabled={busy}
          className="inline-flex items-center gap-2 rounded-md px-5 py-2 text-sm font-semibold text-white disabled:opacity-50"
          style={{ background: "var(--primary-hex)" }}
          data-testid="smart-fill-submit"
        >
          {busy ? <><Loader2 className="h-4 w-4 animate-spin" /> Saving…</> : <>Fill & save</>}
        </button>
      </div>
    </div>
  );
}


// ── Live PDF Preview ───────────────────────────────────────────────────────
// Renders every page of the source PDF to a canvas + overlays the current
// value of each detected field on top of its rect. Updates as the agent
// types — no server round-trip. The field the agent is editing gets a
// pulsing outline + auto-scrolled into view so eye + finger track together.

function PdfPreview({ pdfBytes, fields, values, autoNames, focusedField }) {
  const [pages, setPages] = useState([]); // {pageIndex, width, height, pdfWidth, pdfHeight, imgUrl, scale}
  const [rendering, setRendering] = useState(true);
  const containerRef = useRef(null);

  // Group fields by page so we only render overlays for the fields on each page.
  const fieldsByPage = useMemo(() => {
    const out = {};
    for (const f of fields) {
      if (f.page === null || !f.rect) continue;
      (out[f.page] ??= []).push(f);
    }
    return out;
  }, [fields]);

  // Render every page once when bytes arrive.
  useEffect(() => {
    if (!pdfBytes) return;
    let cancelled = false;
    setRendering(true);
    (async () => {
      // Slice into a fresh Uint8Array — pdfjs detaches the buffer on load.
      const bytes = new Uint8Array(pdfBytes.slice(0));
      const pdf = await pdfjs.getDocument({ data: bytes }).promise;
      const scale = 1.4;
      const out = [];
      for (let i = 1; i <= pdf.numPages; i++) {
        if (cancelled) return;
        const page = await pdf.getPage(i);
        const viewport = page.getViewport({ scale });
        const canvas = document.createElement("canvas");
        canvas.width = viewport.width;
        canvas.height = viewport.height;
        await page.render({ canvasContext: canvas.getContext("2d"), viewport }).promise;
        out.push({
          pageIndex: i - 1,
          width: viewport.width,
          height: viewport.height,
          pdfWidth: viewport.width / scale,
          pdfHeight: viewport.height / scale,
          scale,
          imgUrl: canvas.toDataURL(),
        });
      }
      if (!cancelled) {
        setPages(out);
        setRendering(false);
      }
    })().catch(() => { if (!cancelled) setRendering(false); });
    return () => { cancelled = true; };
  }, [pdfBytes]);

  // Scroll the focused field into view so the agent sees the exact spot on
  // the page they're editing. Debounced by React's own render cycle — the
  // effect fires once per focus change.
  useEffect(() => {
    if (!focusedField || !containerRef.current) return;
    const el = containerRef.current.querySelector(`[data-testid="pdf-preview-overlay-${focusedField}"]`);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
  }, [focusedField, pages.length]);

  if (!pdfBytes) {
    return (
      <div className="rounded-md border border-slate-200 bg-slate-50 flex items-center justify-center text-xs text-slate-500 min-h-[240px]">
        Preview unavailable
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      className="lg:max-h-[62vh] max-h-[46vh] overflow-y-auto bg-slate-100 rounded-md p-3 space-y-3 border border-slate-200"
      data-testid="pdf-preview"
    >
      <div className="flex items-center justify-between text-[10px] font-semibold uppercase tracking-widest text-slate-500 px-1">
        <span>Live preview</span>
        <span className="text-slate-400 normal-case font-normal">Updates as you edit</span>
      </div>
      {rendering && (
        <div className="text-center text-sm text-slate-500 py-16 flex items-center justify-center gap-2">
          <Loader2 className="h-4 w-4 animate-spin" /> Rendering preview…
        </div>
      )}
      {!rendering && pages.map(pg => (
        <div
          key={pg.pageIndex}
          className="relative mx-auto shadow ring-1 ring-slate-200 bg-white"
          style={{ width: pg.width, height: pg.height }}
          data-testid={`pdf-preview-page-${pg.pageIndex}`}
        >
          <img src={pg.imgUrl} alt="" className="absolute inset-0 w-full h-full pointer-events-none select-none" draggable={false} />
          {(fieldsByPage[pg.pageIndex] || []).map(f => {
            const [x1, y1, x2, y2] = f.rect;
            // PDF-space (bottom-left origin) → canvas-space (top-left origin).
            const left = x1 * pg.scale;
            const top = (pg.pdfHeight - y2) * pg.scale;
            const width = (x2 - x1) * pg.scale;
            const height = (y2 - y1) * pg.scale;
            const val = values[f.name] || "";
            const isAuto = autoNames.has(f.name);
            const isFocused = focusedField === f.name;
            const fontPx = Math.max(9, Math.min(height * 0.7, 18));
            return (
              <div
                key={f.name}
                className={`absolute overflow-hidden flex items-center px-1 ${isFocused ? "ring-2 ring-offset-1 z-10" : ""}`}
                style={{
                  left, top, width, height,
                  background: val
                    ? (isAuto ? "rgba(254, 243, 199, 0.75)" : "rgba(219, 234, 254, 0.65)")
                    : "transparent",
                  outline: isFocused ? `2px solid var(--primary-hex)` : "none",
                }}
                title={`${f.label}${val ? ` — ${val}` : ""}`}
                data-testid={`pdf-preview-overlay-${f.name}`}
              >
                <span
                  className="truncate leading-none"
                  style={{
                    fontSize: `${fontPx}px`,
                    color: val ? "#0f172a" : "#94a3b8",
                    fontFamily: "Helvetica, Arial, sans-serif",
                  }}
                >
                  {val || "·"}
                </span>
              </div>
            );
          })}
        </div>
      ))}
    </div>
  );
}



// ── Subject Search Bar ─────────────────────────────────────────────────────
// A single input that queries /subjects/search on debounce and renders
// Leads / Clients / Transactions in categorized groups. Pick one → parent
// runs the prefill call.

const SUBJECT_ICON = {
  lead: User,
  client: Users,
  transaction: FileSignature,
};

function SubjectSearchBar({ onSelect, activeSubject, onClear, loading, label, placeholder, testidPrefix }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState({ leads: [], clients: [], transactions: [] });
  const [open, setOpen] = useState(false);
  const [searching, setSearching] = useState(false);
  const wrapperRef = useRef(null);
  const prefix = testidPrefix || "subject-search";

  useEffect(() => {
    if (activeSubject) {
      setQ(activeSubject.title || "");
    }
  }, [activeSubject]);

  // Close dropdown when clicking outside.
  useEffect(() => {
    const onDoc = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  // Debounced search.
  useEffect(() => {
    const term = q.trim();
    if (!term || activeSubject?.title === term) {
      setResults({ leads: [], clients: [], transactions: [] });
      return;
    }
    setSearching(true);
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get(`/subjects/search`, { params: { q: term, limit: 6 } });
        setResults({ leads: data.leads || [], clients: data.clients || [], transactions: data.transactions || [] });
        setOpen(true);
      } catch {
        setResults({ leads: [], clients: [], transactions: [] });
      } finally { setSearching(false); }
    }, 220);
    return () => clearTimeout(t);
  }, [q, activeSubject]);

  const totalCount = results.leads.length + results.clients.length + results.transactions.length;

  const pick = (s) => {
    onSelect(s);
    setOpen(false);
  };

  const clearAll = () => {
    setQ("");
    setResults({ leads: [], clients: [], transactions: [] });
    setOpen(false);
    onClear?.();
  };

  return (
    <div className="relative" ref={wrapperRef} data-testid={`${prefix}-wrapper`}>
      <label className="text-[11px] font-semibold uppercase tracking-widest text-slate-500 block mb-1.5 flex items-center gap-1.5">
        {label || (<><Search className="h-3 w-3" /> Search for subject · Lead, Client, or Transaction</>)}
      </label>
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 pointer-events-none" />
        <input
          value={q}
          onChange={e => { setQ(e.target.value); setOpen(true); }}
          onFocus={() => q && setOpen(true)}
          placeholder={placeholder || "Try 'Emma', 'Paradise Valley', or a phone number…"}
          className="w-full rounded-md border border-slate-300 pl-9 pr-24 py-2.5 text-sm focus-brand"
          data-testid={`${prefix}-input`}
        />
        <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-1">
          {(searching || loading) && <Loader2 className="h-3.5 w-3.5 animate-spin text-slate-400" />}
          {q && (
            <button
              onClick={clearAll}
              className="text-slate-400 hover:text-slate-700 p-1 rounded"
              title="Clear subject"
              data-testid={`${prefix}-clear`}
            >
              <X className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
      </div>

      {open && q.trim() && (
        <div className="absolute z-30 mt-1 w-full rounded-md border border-slate-200 bg-white shadow-lg max-h-72 overflow-y-auto" data-testid={`${prefix}-results`}>
          {totalCount === 0 && !searching && (
            <div className="p-4 text-sm text-slate-500 text-center">No matches. Try a different name, email, or address.</div>
          )}
          {searching && totalCount === 0 && (
            <div className="p-4 text-sm text-slate-500 text-center flex items-center justify-center gap-2">
              <Loader2 className="h-3.5 w-3.5 animate-spin" /> Searching…
            </div>
          )}
          <SubjectGroup label="Leads"        rows={results.leads}        onPick={pick} testid="leads" prefix={prefix} />
          <SubjectGroup label="Clients"      rows={results.clients}      onPick={pick} testid="clients" prefix={prefix} />
          <SubjectGroup label="Transactions" rows={results.transactions} onPick={pick} testid="transactions" prefix={prefix} />
        </div>
      )}
    </div>
  );
}

function SubjectGroup({ label, rows, onPick, testid, prefix }) {
  if (rows.length === 0) return null;
  const p = prefix || "subject-search";
  return (
    <div className="border-b border-slate-100 last:border-b-0" data-testid={`${p}-group-${testid}`}>
      <div className="px-3 py-1.5 text-[10px] font-semibold uppercase tracking-widest text-slate-500 bg-slate-50">{label}</div>
      {rows.map(s => {
        const Icon = SUBJECT_ICON[s.type] || User;
        return (
          <button
            key={`${s.type}-${s.id}`}
            onClick={() => onPick(s)}
            className="w-full text-left px-3 py-2.5 flex items-start gap-2.5 hover:bg-slate-50 border-t border-slate-100 first:border-t-0"
            data-testid={`${p}-result-${s.type}-${s.id}`}
          >
            <Icon className="h-4 w-4 text-slate-500 mt-0.5 shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold text-slate-900 truncate">{s.title}</div>
              {s.subtitle && <div className="text-xs text-slate-500 truncate">{s.subtitle}</div>}
            </div>
          </button>
        );
      })}
    </div>
  );
}


// ── Manual Overlay ─────────────────────────────────────────────────────────

function ManualOverlayPanel({ docId, pdfBytes, onFilled }) {
  const [pages, setPages] = useState([]); // { pageIndex, width, height, imgUrl }
  const [overlays, setOverlays] = useState([]); // { id, page, xPct, yPct, text, fontSize }
  const [selectedId, setSelectedId] = useState(null);
  const [busy, setBusy] = useState(false);

  // Render every page to a PNG so we can overlay text boxes as absolutely
  // positioned <input> elements. PDF.js gives us page pixel dimensions we
  // use to convert click coords into PDF points.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      // pdfjs.getDocument transfers the ArrayBuffer to a worker, which
      // detaches it — meaning a second effect call (React StrictMode) or
      // any later re-use crashes. Slice into a fresh Uint8Array so the
      // original buffer stays intact.
      const bytes = new Uint8Array(pdfBytes.slice(0));
      const loading = pdfjs.getDocument({ data: bytes });
      const pdf = await loading.promise;
      const out = [];
      for (let i = 1; i <= pdf.numPages; i++) {
        const page = await pdf.getPage(i);
        const viewport = page.getViewport({ scale: 1.5 });
        const canvas = document.createElement("canvas");
        canvas.width = viewport.width;
        canvas.height = viewport.height;
        await page.render({ canvasContext: canvas.getContext("2d"), viewport }).promise;
        const pdfWidth = viewport.width / 1.5;
        const pdfHeight = viewport.height / 1.5;
        out.push({
          pageIndex: i - 1,
          width: viewport.width,
          height: viewport.height,
          pdfWidth,
          pdfHeight,
          imgUrl: canvas.toDataURL(),
        });
      }
      if (!cancelled) setPages(out);
    })();
    return () => { cancelled = true; };
  }, [pdfBytes]);

  const addOverlay = (pageIndex, e, pageEl) => {
    const rect = pageEl.getBoundingClientRect();
    const xPct = (e.clientX - rect.left) / rect.width;
    const yPct = (e.clientY - rect.top) / rect.height;
    const id = crypto.randomUUID();
    setOverlays(prev => [...prev, { id, page: pageIndex, xPct, yPct, text: "Text", fontSize: 12 }]);
    setSelectedId(id);
  };

  const submit = async () => {
    if (!overlays.length) { toast.error("Add at least one field first"); return; }
    setBusy(true);
    try {
      // Convert percentages → PDF points (origin bottom-left).
      const items = overlays.map(o => {
        const pg = pages[o.page];
        const xPoints = o.xPct * pg.pdfWidth;
        const yPoints = (1 - o.yPct) * pg.pdfHeight;
        return { page: o.page, x: xPoints, y: yPoints, text: o.text, font_size: o.fontSize };
      });
      const { data } = await api.post(`/documents/${docId}/manual-overlay`, { overlays: items });
      toast.success("Overlay applied");
      onFilled(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Overlay failed");
    } finally { setBusy(false); }
  };

  const selected = overlays.find(o => o.id === selectedId);

  return (
    <div className="p-4">
      <div className="rounded-md border-l-2 bg-amber-50 text-amber-900 text-sm px-4 py-3 mb-4 flex items-start gap-2" style={{ borderColor: "#D97706" }} data-testid="manual-overlay-note">
        <MousePointer2 className="h-4 w-4 mt-0.5 shrink-0" />
        <div><b>Non-fillable PDF</b> — click anywhere on a page to drop a text field, then type your value. Drag to reposition.</div>
      </div>

      <div className="flex gap-4">
        {/* Canvas */}
        <div className="flex-1 max-h-[60vh] overflow-y-auto bg-slate-100 rounded-md p-3 space-y-4" data-testid="manual-overlay-canvas">
          {pages.length === 0 && (
            <div className="text-center text-sm text-slate-500 py-16 flex items-center justify-center gap-2">
              <Loader2 className="h-4 w-4 animate-spin" /> Rendering PDF…
            </div>
          )}
          {pages.map(pg => (
            <PageCanvas
              key={pg.pageIndex}
              page={pg}
              overlays={overlays.filter(o => o.page === pg.pageIndex)}
              onAdd={(e, el) => addOverlay(pg.pageIndex, e, el)}
              onMove={(id, xPct, yPct) => setOverlays(prev => prev.map(o => o.id === id ? { ...o, xPct, yPct } : o))}
              onSelect={setSelectedId}
              selectedId={selectedId}
              onChangeText={(id, text) => setOverlays(prev => prev.map(o => o.id === id ? { ...o, text } : o))}
            />
          ))}
        </div>

        {/* Sidebar: selected field editor + submit */}
        <aside className="w-64 shrink-0 space-y-3">
          <div className="rounded-md border border-slate-200 bg-white p-3">
            <div className="text-[11px] font-semibold uppercase tracking-widest text-slate-500">Selected field</div>
            {selected ? (
              <div className="mt-2 space-y-2">
                <input
                  value={selected.text}
                  onChange={e => setOverlays(prev => prev.map(o => o.id === selected.id ? { ...o, text: e.target.value } : o))}
                  className="w-full rounded-md border border-slate-300 px-2 py-1.5 text-sm"
                  data-testid="manual-overlay-text-input"
                />
                <label className="text-xs text-slate-500 block">Font size</label>
                <input
                  type="number" min={6} max={72}
                  value={selected.fontSize}
                  onChange={e => setOverlays(prev => prev.map(o => o.id === selected.id ? { ...o, fontSize: Math.max(6, Math.min(72, +e.target.value || 12)) } : o))}
                  className="w-full rounded-md border border-slate-300 px-2 py-1.5 text-sm"
                />
                <button
                  onClick={() => { setOverlays(prev => prev.filter(o => o.id !== selected.id)); setSelectedId(null); }}
                  className="w-full inline-flex items-center justify-center gap-1.5 text-xs font-semibold text-rose-700 hover:bg-rose-50 rounded-md py-1.5"
                >
                  <Trash2 className="h-3.5 w-3.5" /> Remove field
                </button>
              </div>
            ) : (
              <p className="mt-2 text-xs text-slate-500">Click a field on the PDF to edit it.</p>
            )}
          </div>

          <div className="rounded-md border border-slate-200 bg-white p-3 text-xs text-slate-500">
            <div className="font-semibold text-slate-700 mb-1">Fields placed</div>
            <div>{overlays.length}</div>
          </div>

          <button
            onClick={submit}
            disabled={busy || !overlays.length}
            className="w-full inline-flex items-center justify-center gap-2 rounded-md px-4 py-2 text-sm font-semibold text-white disabled:opacity-50"
            style={{ background: "var(--primary-hex)" }}
            data-testid="manual-overlay-submit"
          >
            {busy ? <><Loader2 className="h-4 w-4 animate-spin" /> Saving…</> : <>Fill & save</>}
          </button>
        </aside>
      </div>
    </div>
  );
}

function PageCanvas({ page, overlays, onAdd, onMove, onSelect, selectedId, onChangeText }) {
  const ref = useRef(null);
  const dragState = useRef(null);

  const onMouseDown = (e, id) => {
    e.stopPropagation();
    const rect = ref.current.getBoundingClientRect();
    dragState.current = { id, startX: e.clientX, startY: e.clientY, rect };
    onSelect(id);
    window.addEventListener("mousemove", onDragMove);
    window.addEventListener("mouseup", onDragUp);
  };
  const onDragMove = (e) => {
    const s = dragState.current;
    if (!s) return;
    const xPct = (e.clientX - s.rect.left) / s.rect.width;
    const yPct = (e.clientY - s.rect.top) / s.rect.height;
    onMove(s.id, Math.max(0, Math.min(1, xPct)), Math.max(0, Math.min(1, yPct)));
  };
  const onDragUp = () => {
    dragState.current = null;
    window.removeEventListener("mousemove", onDragMove);
    window.removeEventListener("mouseup", onDragUp);
  };

  return (
    <div
      ref={ref}
      onClick={(e) => onAdd(e, ref.current)}
      className="relative mx-auto shadow ring-1 ring-slate-200 cursor-crosshair"
      style={{ width: page.width, height: page.height, background: "#fff" }}
      data-testid={`manual-overlay-page-${page.pageIndex}`}
    >
      <img src={page.imgUrl} alt="" className="absolute inset-0 w-full h-full pointer-events-none" draggable={false} />
      {overlays.map(o => (
        <div
          key={o.id}
          onMouseDown={(e) => onMouseDown(e, o.id)}
          onClick={(e) => { e.stopPropagation(); onSelect(o.id); }}
          className={`absolute px-1.5 py-0.5 text-black bg-yellow-100/90 ring-1 rounded cursor-move ${selectedId === o.id ? "ring-slate-900" : "ring-slate-300"}`}
          style={{
            left: `${o.xPct * 100}%`,
            top: `${o.yPct * 100}%`,
            fontSize: `${o.fontSize * 1.5}px`,
            transform: "translate(0, -100%)",
          }}
          data-testid={`manual-overlay-item-${o.id}`}
        >
          <input
            value={o.text}
            onChange={e => onChangeText(o.id, e.target.value)}
            onClick={e => e.stopPropagation()}
            onMouseDown={e => e.stopPropagation()}
            className="bg-transparent outline-none min-w-[60px]"
            style={{ fontSize: "inherit", width: `${Math.max(o.text.length, 6)}ch` }}
          />
        </div>
      ))}
    </div>
  );
}
